import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  EMAIL_PROVIDER_LABEL, MODULE, SMTP_PRESETS, outboundEmailTarget,
  type EmailProvider, type OutboundEmailInput, type OutboundEmailSettings, type OutboundEmailTestResult, type OutboundEmailView, type SmtpPresetKey, type SmtpSecurity,
} from '@pqrs/contracts';
import { Icono } from '@xiltrion/ui/icono';
import { Alert, AlertDescription, AlertTitle } from '@xiltrion/ui/alert';
import { Button } from '@xiltrion/ui/button';
import { Checkbox } from '@xiltrion/ui/checkbox';
import { CargaArchivos } from '@xiltrion/ui/bloques/carga-archivos';
import { ConfirmarDialogo } from '@xiltrion/ui/bloques/confirmar';
import { Plegable } from '@xiltrion/ui/bloques/plegable';
import { SelectorSimple } from '@xiltrion/ui/bloques/selectores';
import { Input } from '@xiltrion/ui/input';
import { Label } from '@xiltrion/ui/label';
import { Spinner } from '@xiltrion/ui/spinner';
import { apiDelete, apiGet, apiPost, apiPut } from '@/api/client';
import { Card, ErrorState, LoadingState, PageHeader, fmtDate } from '@/components/ui';
import { errorMessage } from '@/features/cases/lib';
import { SETUP_QUERY_KEY } from '@/features/setup/api';
import { CertifiedEmailCard } from './CertifiedEmailCard';
import { ProviderRadio, SecretField, isEmail, readServiceAccountJson, type ProviderOption } from './email-shared';
import { ConfiguredService, Field, notify, notifyError } from './shared';
import { useSession } from '@/app/session';

const KEY = ['config', 'email', 'outbound'] as const;
export const useOutboundEmail = () => useQuery({ queryKey: KEY, queryFn: () => apiGet<OutboundEmailView>('/communications/outbound') });

const PROVIDERS: Array<ProviderOption<EmailProvider>> = [
  { value: 'MICROSOFT_365', label: EMAIL_PROVIDER_LABEL.MICROSOFT_365, icon: 'mdi:microsoft', description: 'Outlook / Exchange Online, con una aplicación registrada en Microsoft Entra.' },
  { value: 'GOOGLE_WORKSPACE', label: EMAIL_PROVIDER_LABEL.GOOGLE_WORKSPACE, icon: 'mdi:google', description: 'Gmail de la entidad, con una cuenta de servicio y delegación del dominio.' },
  { value: 'SMTP', label: EMAIL_PROVIDER_LABEL.SMTP, icon: 'mdi:server-outline', description: 'Cualquier servidor con usuario y contraseña: Zoho, Amazon SES, SendGrid, Mailgun, Brevo, Postmark o el propio.' },
];
const SECURITY_OPTIONS: Array<{ value: SmtpSecurity; label: string }> = [
  { value: 'starttls', label: 'STARTTLS (587)' },
  { value: 'tls', label: 'TLS directo (465)' },
  { value: 'none', label: 'Sin cifrado (solo red interna)' },
];
const SECRET_LABEL: Record<EmailProvider, string> = { SMTP: 'Contraseña', MICROSOFT_365: 'Secreto del cliente', GOOGLE_WORKSPACE: 'Llave privada' };

interface Form {
  provider: EmailProvider;
  preset: SmtpPresetKey;
  host: string;
  port: string;
  security: SmtpSecurity;
  user: string;
  tenantId: string;
  clientId: string;
  sender: string;
  serviceAccountEmail: string;
  fromName: string;
  fromAddress: string;
  replyTo: string;
  /** Secreto nuevo (vacío = se conserva el guardado). */
  secret: string;
  clearSecret: boolean;
  /** Archivo JSON de la cuenta de servicio de Google del que salió la llave. */
  keyFile: string | null;
}

function initialForm(s: OutboundEmailSettings | null): Form {
  const base: Form = { provider: 'MICROSOFT_365', preset: 'custom', host: '', port: '587', security: 'starttls', user: '', tenantId: '', clientId: '', sender: '', serviceAccountEmail: '', fromName: '', fromAddress: '', replyTo: '', secret: '', clearSecret: false, keyFile: null };
  if (!s) return base;
  const common = { ...base, provider: s.provider, fromName: s.fromName ?? '', fromAddress: s.fromAddress ?? '', replyTo: s.replyTo ?? '' };
  switch (s.provider) {
    case 'SMTP': return { ...common, preset: s.preset, host: s.host, port: String(s.port), security: s.security, user: s.user ?? '' };
    case 'MICROSOFT_365': return { ...common, tenantId: s.tenantId, clientId: s.clientId, sender: s.sender };
    case 'GOOGLE_WORKSPACE': return { ...common, serviceAccountEmail: s.serviceAccountEmail, sender: s.sender };
  }
}

const orNull = (v: string) => v.trim() || null;

/** Lo visible del formulario, con la misma forma que lo guardado (para saber si el secreto guardado aplica). */
function settingsOf(f: Form): OutboundEmailSettings {
  const sender = { fromName: orNull(f.fromName), fromAddress: orNull(f.fromAddress), replyTo: orNull(f.replyTo) };
  switch (f.provider) {
    case 'SMTP': return { provider: 'SMTP', preset: f.preset, host: f.host.trim(), port: Number(f.port), security: f.security, user: orNull(f.user), ...sender, fromAddress: f.fromAddress.trim() };
    case 'MICROSOFT_365': return { provider: 'MICROSOFT_365', tenantId: f.tenantId.trim(), clientId: f.clientId.trim(), sender: f.sender.trim(), ...sender };
    case 'GOOGLE_WORKSPACE': return { provider: 'GOOGLE_WORKSPACE', serviceAccountEmail: f.serviceAccountEmail.trim(), sender: f.sender.trim(), ...sender };
  }
}

/** Sin secreto escrito no se manda el campo (se conserva el guardado); con «quitar», `null`. */
function bodyOf(f: Form): OutboundEmailInput {
  const secretField = { SMTP: 'password', MICROSOFT_365: 'clientSecret', GOOGLE_WORKSPACE: 'privateKey' }[f.provider];
  const secret = f.secret ? { [secretField]: f.secret } : f.clearSecret ? { [secretField]: null } : {};
  return { ...settingsOf(f), ...secret } as OutboundEmailInput;
}

/** Configuración › Plataforma › Correo saliente. */
export function OutboundEmailPage() {
  const certified = useSession().hasModule(MODULE.certifiedMail);
  return (
    <div>
      <PageHeader title="Correo saliente" subtitle="Con este servicio se les escribe a los ciudadanos (radicado, solicitudes de información y respuestas) y se avisa a los funcionarios. Los correos salen a nombre de la entidad y desde su dominio." />
      <OutboundEmailCard />
      {certified && <div className="mt-6"><CertifiedEmailCard /></div>}
    </div>
  );
}

/** Correo saliente de la entidad: con qué servicio y a nombre de quién se escribe. */
export function OutboundEmailCard() {
  const qc = useQueryClient();
  const q = useOutboundEmail();
  const [f, setF] = useState<Form>(() => initialForm(null));
  const [removing, setRemoving] = useState(false);
  /** Con correo saliente guardado se muestra el resumen; el formulario solo al editar. */
  const [editing, setEditing] = useState(false);
  const [keyFiles, setKeyFiles] = useState<File[]>([]);
  const [keyError, setKeyError] = useState<string | null>(null);
  const v = q.data;
  useEffect(() => { if (v) setF(initialForm(v.settings)); }, [v?.updatedAt, v?.source]); // eslint-disable-line react-hooks/exhaustive-deps
  /** Con la suite conectada, el correo lo configura Datalogic allá (GUIA-AGENTES §3.10): aquí solo se ve y se prueba. */
  const locked = !!v?.managedBySuite;
  const summary = (v?.source === 'tenant' || v?.source === 'suite') && !editing;

  const set = (patch: Partial<Form>) => { setF((x) => ({ ...x, ...patch })); test.reset(); };
  const saved = v?.settings ?? null;
  /** El secreto guardado sirve solo si el proveedor y el destino son los mismos. */
  const storedSecretApplies = useMemo(() => {
    if (!saved?.hasSecret || saved.provider !== f.provider) return false;
    try { return outboundEmailTarget(saved) === outboundEmailTarget(settingsOf(f)); } catch { return false; }
  }, [saved, f]);
  const hasSecret = !!f.secret || (storedSecretApplies && !f.clearSecret);
  const needsSecret = f.provider !== 'SMTP' || !!f.user.trim();

  const missing = (() => {
    if (f.provider === 'SMTP') {
      if (!f.host.trim()) return 'el servidor';
      if (!f.port || Number.isNaN(Number(f.port))) return 'el puerto';
      if (!isEmail(f.fromAddress)) return 'la dirección del remitente';
    } else {
      if (f.provider === 'MICROSOFT_365' && (!f.tenantId.trim() || !f.clientId.trim())) return 'los datos de la aplicación';
      if (f.provider === 'GOOGLE_WORKSPACE' && !isEmail(f.serviceAccountEmail)) return 'la cuenta de servicio';
      if (!isEmail(f.sender)) return 'el buzón que envía';
      if (f.fromAddress.trim() && !isEmail(f.fromAddress)) return 'una dirección de remitente válida';
    }
    if (f.replyTo.trim() && !isEmail(f.replyTo)) return 'una dirección válida en «responder a»';
    if (needsSecret && !hasSecret) return SECRET_LABEL[f.provider].toLowerCase();
    return null;
  })();

  const onSaved = (nv: OutboundEmailView, msg: string) => { qc.setQueryData(KEY, nv); qc.invalidateQueries({ queryKey: SETUP_QUERY_KEY }); notify('success', msg); };
  const save = useMutation({ mutationFn: () => apiPut<OutboundEmailView>('/communications/outbound', bodyOf(f)), onSuccess: (nv) => { onSaved(nv, 'Correo saliente guardado'); setEditing(false); }, onError: notifyError });
  const remove = useMutation({ mutationFn: () => apiDelete<OutboundEmailView>('/communications/outbound'), onSuccess: (nv) => { onSaved(nv, 'Correo saliente quitado'); setEditing(false); }, onError: notifyError });
  /** En el resumen se prueba lo guardado (sin mandar configuración); al editar, lo del formulario. */
  const test = useMutation({ mutationFn: (sendTest: boolean) => apiPost<OutboundEmailTestResult>('/communications/outbound/test', { ...(summary ? {} : { config: bodyOf(f) }), sendTest }) });

  /** Del JSON de la cuenta de servicio salen el correo de la cuenta y la llave; el archivo no se guarda. */
  async function readKeyFile(files: File[]) {
    setKeyFiles(files.slice(-1));
    setKeyError(null);
    const file = files.at(-1);
    if (!file) return;
    try {
      const j = await readServiceAccountJson(file);
      set({ serviceAccountEmail: j.clientEmail, secret: j.privateKey, clearSecret: false, keyFile: file.name });
    } catch (e) {
      setKeyError((e as Error).message || 'No se pudo leer el archivo.');
    }
  }

  if (q.isLoading) return <Card title="Servicio y remitente"><LoadingState rows={3} /></Card>;
  if (q.isError || !v) return <Card title="Servicio y remitente"><ErrorState error={q.error} onRetry={() => q.refetch()} /></Card>;
  const blocked = v.secretsKey === 'missing';
  const preset = SMTP_PRESETS[f.preset];
  const cancelEdit = () => { setF(initialForm(saved)); setKeyFiles([]); setKeyError(null); test.reset(); setEditing(false); };

  const testResult = (<>
    {test.data && (test.data.ok
      ? <Alert variant="success"><Icono nombre="mdi:check-circle" /><AlertTitle>{test.data.sentTo ? 'Correo de prueba enviado' : 'La conexión funciona'}</AlertTitle><AlertDescription>{test.data.detail}</AlertDescription></Alert>
      : <Alert variant="destructive"><Icono nombre="mdi:alert-circle-outline" /><AlertTitle>No funcionó</AlertTitle><AlertDescription className="break-words">{test.data.error}</AlertDescription></Alert>)}
    {test.isError && <Alert variant="destructive"><AlertDescription>{errorMessage(test.error)}</AlertDescription></Alert>}
  </>);
  const testButtons = (disabled: boolean) => (<>
    <Button type="button" size="sm" variant="outline" disabled={disabled || test.isPending} onClick={() => test.mutate(false)}>{test.isPending && test.variables === false ? <Spinner /> : <Icono nombre="mdi:lan-connect" />} Probar conexión</Button>
    <Button type="button" size="sm" variant="outline" disabled={disabled || test.isPending} onClick={() => test.mutate(true)}>{test.isPending && test.variables === true ? <Spinner /> : <Icono nombre="mdi:send-outline" />} Enviarme un correo de prueba</Button>
  </>);
  const savedAt = (v.source === 'tenant' || v.source === 'suite') && v.updatedAt && <span className="text-xs text-muted-foreground ml-auto">Guardado {fmtDate(v.updatedAt)}{v.updatedBy ? ` · ${v.updatedBy}` : ''}</span>;
  const removeButton = v.source === 'tenant' && !locked && <Button type="button" size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => setRemoving(true)}><Icono nombre="mdi:trash-can-outline" /> Quitar</Button>;
  const removeDialog = (
    <ConfirmarDialogo abierto={removing} onAbiertoChange={setRemoving} destructivo titulo="Quitar el correo saliente" textoConfirmar="Quitar"
      descripcion="Desde ahora no saldrá ningún correo de la entidad (en producción): los ciudadanos no recibirán su radicado ni las respuestas hasta configurar otro."
      onConfirmar={() => remove.mutateAsync().then(() => undefined)} />
  );

  const suiteNote = locked && (v.source === 'suite'
    ? <Alert variant="info"><Icono nombre="mdi:apps" /><AlertDescription>El correo de la entidad lo configura Datalogic en la suite y lo usan todas sus apps. Para cambiarlo, pídaselo a soporte.</AlertDescription></Alert>
    : v.source === 'tenant'
      ? <Alert variant="warning"><Icono nombre="mdi:swap-horizontal" /><AlertTitle>No se está enviando correo</AlertTitle><AlertDescription>La suite todavía no le asigna a SGDEA el correo de la entidad, y conectado a la suite SGDEA no envía con el suyo: los correos quedan como «No enviada». Datalogic pasa este a la suite con <code>pnpm suite:mail-export</code>.</AlertDescription></Alert>
      : <Alert variant={v.source === 'none' ? 'destructive' : 'info'}><Icono nombre="mdi:apps" /><AlertTitle>La entidad aún no tiene correo en la suite</AlertTitle><AlertDescription>Lo configura Datalogic en la suite (ficha de la entidad › Correo).{v.source === 'development' && v.development ? ` Mientras tanto, en este servidor de pruebas sale por ${v.development.host}:${v.development.port}.` : ''}</AlertDescription></Alert>);

  if (locked && !(summary && saved)) {
    return (
      <Card title="Servicio y remitente">
        <div className="flex flex-col gap-4">
          {suiteNote}
          {testResult}
          {v.source === 'development' && <div className="flex flex-wrap items-center gap-2">{testButtons(false)}</div>}
        </div>
      </Card>
    );
  }

  if (summary && saved) {
    const providerIcon = PROVIDERS.find((p) => p.value === saved.provider)?.icon ?? 'mdi:send-outline';
    const sender = saved.provider === 'SMTP' ? saved.fromAddress : saved.sender;
    const from = `${saved.fromName ? `${saved.fromName} ` : ''}<${saved.fromAddress || sender}>`;
    const access = saved.provider === 'SMTP'
      ? { label: 'Servidor', value: `${saved.host}:${saved.port}${saved.user ? ` · ${saved.user}` : ' · sin autenticación'}` }
      : saved.provider === 'MICROSOFT_365'
        ? { label: 'Aplicación', value: saved.clientId }
        : { label: 'Cuenta de servicio', value: saved.serviceAccountEmail };
    return (
      <Card title="Servicio y remitente">
        <ConfiguredService icon={providerIcon} title={<>{sender} en {EMAIL_PROVIDER_LABEL[saved.provider]}</>}
          items={[
            access,
            { label: 'Remitente', value: from },
            { label: 'Responder a', value: saved.replyTo ?? 'El buzón de entrada' },
          ]}
          actions={<>
            {!locked && <Button type="button" size="sm" variant="outline" onClick={() => { test.reset(); setEditing(true); }}><Icono nombre="mdi:pencil-outline" /> Editar</Button>}
            {testButtons(false)}
            {removeButton}
            {savedAt}
          </>}>
          {suiteNote}
          {testResult}
        </ConfiguredService>
        {removeDialog}
      </Card>
    );
  }

  return (
    <Card title="Servicio y remitente">
      <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); if (!missing) save.mutate(); }}>
        {blocked && <Alert variant="destructive"><Icono nombre="mdi:key-outline" /><AlertTitle>El servidor no tiene SECRETS_KEY</AlertTitle><AlertDescription>Sin la llave maestra no se pueden guardar contraseñas. Hay que configurarla en el servidor.</AlertDescription></Alert>}
        {v.source === 'none' && <Alert variant="destructive"><Icono nombre="mdi:email-off-outline" /><AlertTitle>No sale ningún correo</AlertTitle><AlertDescription>Los ciudadanos no reciben su radicado ni la respuesta hasta configurar el correo saliente.</AlertDescription></Alert>}
        {v.source === 'development' && v.development && <Alert variant="info"><Icono nombre="mdi:server-outline" /><AlertTitle>Se usa el servidor de correo de desarrollo</AlertTitle><AlertDescription>Hoy los correos salen por {v.development.host}:{v.development.port} desde {v.development.from}. En producción no existe: la entidad debe configurar el suyo.</AlertDescription></Alert>}

        <ProviderRadio idPrefix="oe-provider" ariaLabel="Servicio de correo" value={f.provider} options={PROVIDERS} onChange={(p) => set({ provider: p, secret: '', clearSecret: false, keyFile: null })} />

        {f.provider === 'MICROSOFT_365' && (<>
          <Plegable titulo="Cómo obtener estos datos">
            <ol className="text-sm text-muted-foreground m-0 pl-5 space-y-1">
              <li>En el centro de administración de Microsoft Entra, abra Registros de aplicaciones y cree un registro nuevo (solo cuentas de este directorio).</li>
              <li>En Permisos de API agregue Microsoft Graph › Permisos de aplicación › Mail.Send y conceda el consentimiento de administrador.</li>
              <li>En Certificados y secretos cree un secreto de cliente y copie su valor. Anote la fecha en que vence: ese día se debe reemplazar aquí.</li>
              <li>Recomendado: limite la aplicación al buzón de solicitudes en Exchange Online (RBAC para aplicaciones). Sin eso puede enviar como cualquier buzón de la entidad.</li>
            </ol>
          </Plegable>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Id del directorio (inquilino)" htmlFor="oe-tenant"><Input id="oe-tenant" autoComplete="off" placeholder="entidad.onmicrosoft.com o GUID" value={f.tenantId} onChange={(e) => set({ tenantId: e.target.value })} /></Field>
            <Field label="Id de la aplicación (cliente)" htmlFor="oe-client"><Input id="oe-client" autoComplete="off" placeholder="00000000-0000-0000-0000-000000000000" value={f.clientId} onChange={(e) => set({ clientId: e.target.value })} /></Field>
            <SecretField id="oe-secret" label={SECRET_LABEL[f.provider]} value={f.secret} onChange={(secret) => set({ secret })} stored={storedSecretApplies} />
            <Field label="Buzón que envía" htmlFor="oe-sender" hint="Un buzón con licencia, por ejemplo pqrs@entidad.gov.co."><Input id="oe-sender" type="email" autoComplete="off" value={f.sender} onChange={(e) => set({ sender: e.target.value })} /></Field>
          </div>
        </>)}

        {f.provider === 'GOOGLE_WORKSPACE' && (<>
          <Plegable titulo="Cómo obtener estos datos">
            <ol className="text-sm text-muted-foreground m-0 pl-5 space-y-1">
              <li>En Google Cloud, en un proyecto de la entidad, habilite la API de Gmail y cree una cuenta de servicio. En Claves agregue una clave nueva de tipo JSON.</li>
              <li>En la Consola de administración de Google Workspace › Seguridad › Controles de API › Delegación de todo el dominio, agregue el id de cliente de la cuenta de servicio con el permiso https://www.googleapis.com/auth/gmail.send (solo enviar).</li>
              <li>Cargue aquí el JSON e indique el usuario desde el que se envía.</li>
            </ol>
          </Plegable>
          <CargaArchivos archivos={keyFiles} onArchivosChange={readKeyFile} accept=".json,application/json" maxTamano={64 * 1024} textoAyuda={storedSecretApplies && !f.keyFile ? 'Hay una llave guardada; cargue otro JSON solo para cambiarla.' : 'JSON de la cuenta de servicio. Se guarda solo la llave, cifrada.'} aria-label="Cargar el JSON de la cuenta de servicio" />
          {keyError && <Alert variant="destructive"><AlertDescription>{keyError}</AlertDescription></Alert>}
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Cuenta de servicio" htmlFor="oe-sa" hint={f.keyFile ? `Llave tomada de ${f.keyFile}.` : undefined}><Input id="oe-sa" type="email" autoComplete="off" value={f.serviceAccountEmail} onChange={(e) => set({ serviceAccountEmail: e.target.value })} /></Field>
            <Field label="Usuario que envía" htmlFor="oe-sender" hint="Un usuario real del dominio, por ejemplo pqrs@entidad.gov.co."><Input id="oe-sender" type="email" autoComplete="off" value={f.sender} onChange={(e) => set({ sender: e.target.value })} /></Field>
          </div>
          {storedSecretApplies && !f.keyFile && <div className="flex items-center gap-2"><Checkbox id="oe-clear" checked={f.clearSecret} onCheckedChange={(c) => set({ clearSecret: !!c })} /><Label htmlFor="oe-clear" className="font-normal">Quitar la llave guardada</Label></div>}
        </>)}

        {f.provider === 'SMTP' && (<>
          <Field label="Servicio" htmlFor="oe-preset" hint={preset.hint}>
            <SelectorSimple id="oe-preset" value={f.preset} options={Object.entries(SMTP_PRESETS).map(([value, p]) => ({ value, label: p.label }))}
              onChange={(k) => { if (!k) return; const p = SMTP_PRESETS[k as SmtpPresetKey]; set({ preset: k as SmtpPresetKey, ...(k === 'custom' ? {} : { host: p.host, port: String(p.port), security: p.security }), ...('user' in p ? { user: p.user } : {}) }); }} />
          </Field>
          {f.preset === 'office365' && <Alert variant="warning"><Icono nombre="mdi:alert-outline" /><AlertDescription>Exchange Online ya no acepta usuario y contraseña por SMTP. Use la opción Microsoft 365.</AlertDescription></Alert>}
          <div className="grid sm:grid-cols-[1fr_8rem] gap-3">
            <Field label="Servidor" htmlFor="oe-host"><Input id="oe-host" autoComplete="off" placeholder="smtp.entidad.gov.co" value={f.host} onChange={(e) => set({ host: e.target.value })} /></Field>
            <Field label="Puerto" htmlFor="oe-port"><Input id="oe-port" className="tnum" type="number" inputMode="numeric" min={1} max={65535} value={f.port} onChange={(e) => set({ port: e.target.value })} /></Field>
          </div>
          <Field label="Seguridad" htmlFor="oe-security"><SelectorSimple id="oe-security" value={f.security} options={SECURITY_OPTIONS} onChange={(s) => s && set({ security: s as SmtpSecurity })} /></Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Usuario" htmlFor="oe-user" hint="Vacío si el servidor no pide autenticación."><Input id="oe-user" autoComplete="off" value={f.user} onChange={(e) => set({ user: e.target.value })} /></Field>
            <SecretField id="oe-secret" label={SECRET_LABEL[f.provider]} value={f.secret} onChange={(secret) => set({ secret })} stored={storedSecretApplies} clearable clear={f.clearSecret} onClear={(c) => set({ clearSecret: c, secret: '' })} />
          </div>
        </>)}

        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Nombre del remitente" htmlFor="oe-from-name" hint="Vacío: el nombre de la entidad."><Input id="oe-from-name" value={f.fromName} onChange={(e) => set({ fromName: e.target.value })} /></Field>
          <Field label="Dirección del remitente" htmlFor="oe-from" required={f.provider === 'SMTP'} hint={f.provider === 'SMTP' ? 'Debe estar permitida en el servicio (dominio verificado).' : 'Vacío: el buzón que envía.'}><Input id="oe-from" type="email" value={f.fromAddress} onChange={(e) => set({ fromAddress: e.target.value })} /></Field>
          <Field label="Responder a" htmlFor="oe-reply" hint="Vacío: el buzón de entrada, para que las respuestas vuelvan al caso."><Input id="oe-reply" type="email" value={f.replyTo} onChange={(e) => set({ replyTo: e.target.value })} /></Field>
        </div>

        {testResult}
        {missing && <p className="text-xs text-muted-foreground m-0">Falta {missing}.</p>}

        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" size="sm" disabled={!!missing || save.isPending || blocked}>{save.isPending ? <Spinner /> : <Icono nombre="mdi:check" />} Guardar</Button>
          {editing && <Button type="button" size="sm" variant="ghost" onClick={cancelEdit}>Cancelar</Button>}
          {testButtons(!!missing)}
          {removeButton}
          {savedAt}
        </div>
      </form>
      {removeDialog}
    </Card>
  );
}
